import React, { useState, useRef } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { parseAddress, isValidEmail } from '../lib/emailAddresses';

/**
 * Chip-style multi-recipient email input.
 *
 * Props:
 *   value      – string[] of emails
 *   onChange   – (next: string[]) => void
 *   placeholder, disabled, dataTestId
 *   suggestions – string[] (e.g. thread participants) shown when input is empty
 */
export default function RecipientInput({
  value = [],
  onChange,
  placeholder = '',
  disabled = false,
  dataTestId,
  suggestions = [],
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const addEmail = (raw) => {
    const cleaned = parseAddress(raw) || raw.trim();
    if (!cleaned) return;
    if (!isValidEmail(cleaned)) {
      setError(`"${cleaned}" is not a valid email`);
      return;
    }
    if (value.includes(cleaned)) {
      setError(`${cleaned} already added`);
      return;
    }
    setError(null);
    onChange([...value, cleaned]);
    setDraft('');
  };

  const removeAt = (idx) => {
    setError(null);
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKey = (e) => {
    if (['Enter', 'Tab', ',', ';'].includes(e.key)) {
      if (draft.trim()) {
        e.preventDefault();
        addEmail(draft);
      }
    } else if (e.key === 'Backspace' && !draft && value.length) {
      removeAt(value.length - 1);
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text && /[,;\s]/.test(text)) {
      e.preventDefault();
      // Split on commas / semicolons / whitespace
      const tokens = text.split(/[,;\s]+/).filter(Boolean);
      const valid = [];
      for (const t of tokens) {
        const cleaned = parseAddress(t) || t.trim();
        if (isValidEmail(cleaned) && !value.includes(cleaned) && !valid.includes(cleaned)) {
          valid.push(cleaned);
        }
      }
      if (valid.length) onChange([...value, ...valid]);
    }
  };

  const handleBlur = () => {
    if (draft.trim()) addEmail(draft);
  };

  const remainingSuggestions = suggestions.filter(s => !value.includes(s));

  return (
    <div className="flex-1" data-testid={dataTestId}>
      <div className="flex items-center flex-wrap gap-1 min-h-[28px]">
        {value.map((addr, idx) => (
          <span
            key={`${addr}-${idx}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/20"
            data-testid={`recipient-chip-${idx}`}
          >
            <span className="font-mono text-[11px]">{addr}</span>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              disabled={disabled}
              className="hover:text-red-600 transition-colors disabled:opacity-40"
              aria-label={`Remove ${addr}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="email"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value.length ? '' : placeholder}
          className="flex-1 min-w-[180px] text-sm bg-transparent border-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground/40 px-1 py-0.5"
        />
      </div>

      {/* Suggestions row — visible when there are unused thread participants */}
      {!disabled && remainingSuggestions.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">From thread:</span>
          {remainingSuggestions.slice(0, 6).map((addr) => (
            <button
              key={addr}
              type="button"
              onClick={() => addEmail(addr)}
              className="px-1.5 py-0.5 rounded-md border border-dashed border-border bg-white text-[11px] text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
              data-testid={`recipient-suggestion-${addr}`}
              title={`Add ${addr}`}
            >
              + {addr}
            </button>
          ))}
          {remainingSuggestions.length > 6 && (
            <button
              type="button"
              onClick={() => onChange([...value, ...remainingSuggestions])}
              className="text-[11px] text-primary hover:underline"
            >
              + add all {remainingSuggestions.length}
            </button>
          )}
        </div>
      )}

      {/* Inline error */}
      {error && (
        <p className="mt-1 text-[11px] text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />{error}
        </p>
      )}
    </div>
  );
}
